import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Power, Archive, Trash2, PlayCircle, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { useTemplateLifecycle } from '@/api/hooks/useTemplateLifecycle';
import { apiClient } from '@/api/client';

type ConfirmAction = 'disable' | 'enable' | 'archive' | 'hard_delete';

interface Props {
  templateId: string;
  lifecycleState: string;
  isHubTemplate: boolean;
  tenantId: string;
  activeAssessmentCount: number;
}

export function TemplateLifecycleActions({
  templateId,
  lifecycleState,
  isHubTemplate,
  tenantId,
  activeAssessmentCount,
}: Props) {
  const navigate = useNavigate();
  const [pendingAction, setPendingAction] = useState<ConfirmAction | null>(null);
  const lifecycle = useTemplateLifecycle(tenantId);

  async function execute(action: ConfirmAction) {
    setPendingAction(null);

    if (action === 'hard_delete') {
      try {
        await apiClient.delete(`/assessment-templates/${templateId}`);
        toast.success('Template deleted.');
        navigate('/templates', { replace: true });
      } catch (err: unknown) {
        const e = err as { response?: { data?: { errors?: { code?: string; active_count?: number } } } };
        const code = e?.response?.data?.errors?.code;
        if (code === 'TEMPLATE_HAS_ACTIVE_ASSESSMENTS') {
          const n = e?.response?.data?.errors?.active_count ?? '?';
          toast.error(`Cannot delete — ${n} active assessment(s) reference this template.`);
        } else {
          toast.error('Delete failed. Please try again.');
        }
      }
      return;
    }

    const nextStateMap: Record<Exclude<ConfirmAction, 'hard_delete'>, 'published' | 'disabled' | 'archived'> = {
      disable: 'disabled',
      enable: 'published',
      archive: 'archived',
    };

    try {
      await lifecycle.mutateAsync({ templateId, next_state: nextStateMap[action] });
      const label =
        action === 'disable' ? 'disabled' :
        action === 'enable' ? 're-enabled' :
        'archived';
      toast.success(`Template ${label}.`);
    } catch {
      toast.error('Action failed. Please try again.');
    }
  }

  if (isHubTemplate || lifecycleState === 'archived' || lifecycleState === 'draft') return null;

  return (
    <>
      <div className="flex flex-wrap gap-2">
        {lifecycleState === 'published' && (
          <Button size="sm" variant="outline" onClick={() => setPendingAction('disable')}>
            <Power className="mr-1.5 h-3.5 w-3.5" />
            Disable
          </Button>
        )}
        {lifecycleState === 'disabled' && (
          <Button size="sm" variant="outline" onClick={() => setPendingAction('enable')}>
            <PlayCircle className="mr-1.5 h-3.5 w-3.5" />
            Re-enable
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={() => setPendingAction('archive')}>
          <Archive className="mr-1.5 h-3.5 w-3.5" />
          Archive
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="text-destructive border-destructive/40 hover:bg-destructive/10"
          onClick={() => setPendingAction('hard_delete')}
          disabled={activeAssessmentCount > 0}
          title={
            activeAssessmentCount > 0
              ? `${activeAssessmentCount} active assessment(s) — cannot delete`
              : undefined
          }
        >
          <Trash2 className="mr-1.5 h-3.5 w-3.5" />
          Delete
        </Button>
      </div>

      {pendingAction && (
        <LifecycleConfirmDialog
          action={pendingAction}
          isPending={lifecycle.isPending}
          onConfirm={() => execute(pendingAction)}
          onCancel={() => setPendingAction(null)}
        />
      )}
    </>
  );
}

// ── Inline confirm dialog ─────────────────────────────────────────────────────

const TITLES: Record<ConfirmAction, string> = {
  disable: 'Disable Template',
  enable: 'Re-enable Template',
  archive: 'Archive Template',
  hard_delete: 'Delete Template',
};

const BODIES: Record<ConfirmAction, string> = {
  disable:
    'This template will no longer be available for new assessments. In-progress assessments are unaffected.',
  enable: 'This template will become available for new assessments again.',
  archive:
    'Archived templates cannot be used for new assessments and cannot be un-archived. This is permanent.',
  hard_delete:
    'This will permanently delete the template and all its sections and questions. This cannot be undone.',
};

function LifecycleConfirmDialog({
  action,
  isPending,
  onConfirm,
  onCancel,
}: {
  action: ConfirmAction;
  isPending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const isDangerous = action === 'archive' || action === 'hard_delete';

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onCancel(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <div className="flex items-center gap-2">
            {isDangerous && <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />}
            <DialogTitle>{TITLES[action]}</DialogTitle>
          </div>
        </DialogHeader>
        <p className="text-sm text-text-secondary py-1">{BODIES[action]}</p>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={isPending}>
            Cancel
          </Button>
          <Button
            className={isDangerous ? 'bg-destructive hover:bg-destructive/90 text-destructive-foreground' : ''}
            onClick={onConfirm}
            disabled={isPending}
          >
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {action === 'disable' ? 'Disable' :
             action === 'enable' ? 'Re-enable' :
             action === 'archive' ? 'Archive' :
             'Delete Permanently'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
